import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  useColorModeValue,
  Divider,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { FiPlay, FiInfo, FiCheckCircle } from 'react-icons/fi'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import {
  refundService,
  type RefundVerificationTriggerRequest,
  type RefundVerificationTriggerResponse,
} from '@/services/admin/refundService'
import { useToast } from '@/hooks/useToast'
import { getChainsByNetwork } from '@/constants/chains'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { DropdownSelect } from '@/components/common/DropdownSelect'

export function RefundVerificationTrigger() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const chainOptions = useMemo(
    () => [
      { id: '', name: t('monitoring.confirmations.chains.all', 'All Chains') },
      ...getChainsByNetwork(currentEnv).map(chain => ({
        id: String(chain.numericChainId),
        name: chain.shortName,
      })),
    ],
    [currentEnv, t]
  )

  // Form state
  const [merchantId, setMerchantId] = useState('')
  const [orderIds, setOrderIds] = useState('')
  const [chainId, setChainId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [force, setForce] = useState(false)

  // Validation errors
  const [merchantIdError, setMerchantIdError] = useState('')
  const [orderIdsError, setOrderIdsError] = useState('')

  // Results state
  const [lastResults, setLastResults] = useState<RefundVerificationTriggerResponse | null>(null)

  const isValidMerchantIdentifier = (value: string): boolean =>
    value.trim().length === 0 || /^[A-Za-z0-9_-]+$/.test(value.trim())

  // Mutation for triggering verification
  const triggerMutation = useMutation({
    mutationFn: async () => {
      // Validate inputs
      let hasError = false

      if (!isValidMerchantIdentifier(merchantId)) {
        setMerchantIdError(t('refunds.trigger.errors.invalidMerchantId'))
        hasError = true
      } else {
        setMerchantIdError('')
      }

      // Validate order IDs if provided
      let parsedOrderIds: string[] | undefined
      if (orderIds.trim()) {
        parsedOrderIds = parseMultilineEntries(orderIds)

        const invalidOrderIds = findEntriesWithoutPrefix(parsedOrderIds, 'ord_')
        if (invalidOrderIds.length > 0) {
          setOrderIdsError(t('refunds.trigger.errors.invalidOrderIds', {
            ids: invalidOrderIds.slice(0, 3).join(', ') + (invalidOrderIds.length > 3 ? '...' : '')
          }))
          hasError = true
        } else {
          setOrderIdsError('')
        }
      } else {
        setOrderIdsError('')
      }

      if (hasError) {
        throw new Error(t('refunds.trigger.errors.validationFailed'))
      }

      // Build request params
      const params: RefundVerificationTriggerRequest = {}
      const normalizedMerchantId = merchantId.trim()
      if (normalizedMerchantId) params.merchantId = normalizedMerchantId
      if (parsedOrderIds && parsedOrderIds.length > 0) params.orderIds = parsedOrderIds
      if (chainId) params.chainId = chainId
      if (dateFrom) params.dateFrom = dateFrom
      if (dateTo) params.dateTo = dateTo
      if (force) params.force = force

      const result = await refundService.triggerRefundVerification(params, currentEnv)
      return result
    },
    onSuccess: (data) => {
      setLastResults(data)
      toast.success({
        title: t('refunds.trigger.success.title'),
        description: data.message || t('refunds.trigger.success.description'),
        id: 'refund-verification-success',
      })

      // Clear form on success
      setMerchantId('')
      setOrderIds('')
      setChainId('')
      setDateFrom('')
      setDateTo('')
      setForce(false)
    },
    onError: (error: any) => {
      toast.error({
        title: t('refunds.trigger.error.title'),
        description: error.message || t('refunds.trigger.error.description'),
        id: 'refund-verification-error',
      })
    },
  })

  const handleSubmit = () => {
    triggerMutation.mutate()
  }

  const handleMerchantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantId(e.target.value)
    if (merchantIdError) setMerchantIdError('')
  }

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOrderIds(e.target.value)
    if (orderIdsError) setOrderIdsError('')
  }

  return (
    <Card bg={cardBg} borderColor={borderColor}>
      <CardHeader>
        <HStack justify="space-between">
          <HStack>
            <Icon as={FiPlay} color="blue.500" />
            <Heading size="md">{t('refunds.trigger.title')}</Heading>
          </HStack>
          <Badge colorScheme="blue" variant="subtle">
            {t('refunds.trigger.badge')}
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Description */}
          <Alert status="info" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">{t('refunds.trigger.info.title')}</AlertTitle>
              <AlertDescription fontSize="xs">
                {t('refunds.trigger.info.description')}
              </AlertDescription>
            </Box>
          </Alert>

          {/* Form Fields */}
          <VStack spacing={4} align="stretch">
            {/* Merchant ID Field */}
            <FormControl isInvalid={!!merchantIdError}>
              <FormLabel fontSize="sm">{t('refunds.trigger.fields.merchantId.label')}</FormLabel>
              <Input
                placeholder={t('refunds.trigger.fields.merchantId.placeholder')}
                value={merchantId}
                onChange={handleMerchantIdChange}
                size="sm"
                isDisabled={triggerMutation.isPending}
              />
              <FormHelperText fontSize="xs">
                {t('refunds.trigger.fields.merchantId.helper')}
              </FormHelperText>
              {merchantIdError && (
                <FormErrorMessage fontSize="xs">{merchantIdError}</FormErrorMessage>
              )}
            </FormControl>

            {/* Order IDs Field */}
            <FormControl isInvalid={!!orderIdsError}>
              <FormLabel fontSize="sm">{t('refunds.trigger.fields.orderIds.label')}</FormLabel>
              <Textarea
                placeholder={t('refunds.trigger.fields.orderIds.placeholder')}
                value={orderIds}
                onChange={handleOrderIdsChange}
                size="sm"
                rows={3}
                isDisabled={triggerMutation.isPending}
              />
              <FormHelperText fontSize="xs">
                {t('refunds.trigger.fields.orderIds.helper')}
              </FormHelperText>
              {orderIdsError && (
                <FormErrorMessage fontSize="xs">{orderIdsError}</FormErrorMessage>
              )}
            </FormControl>

            {/* Date Range Fields */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('refunds.trigger.fields.dateFrom.label')}</FormLabel>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  size="sm"
                  isDisabled={triggerMutation.isPending}
                />
                <FormHelperText fontSize="xs">
                  {t('refunds.trigger.fields.dateFrom.helper')}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">{t('refunds.trigger.fields.dateTo.label')}</FormLabel>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  size="sm"
                  isDisabled={triggerMutation.isPending}
                />
                <FormHelperText fontSize="xs">
                  {t('refunds.trigger.fields.dateTo.helper')}
                </FormHelperText>
              </FormControl>
            </SimpleGrid>

            {/* Chain Selection */}
            <FormControl>
              <FormLabel fontSize="sm">{t('refunds.trigger.fields.chain.label')}</FormLabel>
              <DropdownSelect
                value={chainId}
                options={chainOptions.map(chain => ({
                  value: chain.id,
                  label: chain.name,
                }))}
                onChange={setChainId}
                ariaLabel={t('refunds.trigger.fields.chain.label')}
                size="sm"
                isDisabled={triggerMutation.isPending}
              />
              <FormHelperText fontSize="xs">
                {t('refunds.trigger.fields.chain.helper')}
              </FormHelperText>
            </FormControl>

            {/* Force Option */}
            <FormControl>
              <HStack justify="space-between">
                <HStack>
                  <FormLabel fontSize="sm" mb={0}>
                    {t('refunds.trigger.fields.force.label')}
                  </FormLabel>
                  <Tooltip
                    label={t('refunds.trigger.fields.force.tooltip')}
                    placement="top"
                  >
                    <Box>
                      <Icon as={FiInfo} color="gray.500" boxSize={3} />
                    </Box>
                  </Tooltip>
                </HStack>
                <Switch
                  isChecked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  isDisabled={triggerMutation.isPending}
                  colorScheme="blue"
                />
              </HStack>
              <FormHelperText fontSize="xs">
                {t('refunds.trigger.fields.force.helper')}
              </FormHelperText>
            </FormControl>
          </VStack>

          {/* Submit Button */}
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={triggerMutation.isPending}
            loadingText={t('refunds.trigger.button.loading')}
            leftIcon={<FiPlay />}
            size="md"
          >
            {t('refunds.trigger.button.submit')}
          </Button>

          {/* Results Display */}
          {lastResults && lastResults.results && (
            <>
              <Divider />
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Icon as={FiCheckCircle} color="green.500" />
                  <Heading size="sm">{t('refunds.trigger.results.title')}</Heading>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('refunds.trigger.results.refundsFound')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="gray.600">
                      {lastResults.results.refundsFound}
                    </StatNumber>
                  </Stat>

                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('refunds.trigger.results.refundsQueued')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="blue.500">
                      {lastResults.results.refundsQueued}
                    </StatNumber>
                  </Stat>

                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('refunds.trigger.results.refundsSkipped')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="orange.500">
                      {lastResults.results.refundsSkipped}
                    </StatNumber>
                  </Stat>

                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('refunds.trigger.results.queueErrors')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="red.500">
                      {lastResults.results.queueErrors}
                    </StatNumber>
                  </Stat>
                </SimpleGrid>

                {/* Success/Warning Message */}
                {lastResults.results.queueErrors > 0 || lastResults.results.refundsSkipped > 0 ? (
                  <Alert status="warning" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('refunds.trigger.results.warningMessage')}
                    </AlertDescription>
                  </Alert>
                ) : lastResults.results.refundsFound === 0 ? (
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('refunds.trigger.results.noMatchesMessage')}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert status="success" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('refunds.trigger.results.successMessage')}
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
